// LIBRERIE/IMPORTS
import React from 'react';
import { useParams } from 'react-router-dom';

// CONFIGURAZIONI
import { AGENCY_ID, appConfig } from '../../config/appConfig';

// COMPONENTI
import Privacy from './Routes/Privacy';
import Cookies from './Routes/Cookies';

// Interfaccia per la mappa degli ID Policy (utile per type safety)
interface PolicyIdMap {
    [key: string]: number;
}

/**
 * Funzione helper per ottenere l'ID della policy Iubenda corretto per la lingua data.
 * Include logica di fallback.
 * @param lng - Il codice lingua (es. 'it', 'en') o undefined.
 * @returns L'ID numerico della policy.
 */
export const getPolicyIdForLang = (lng: string | undefined): number => {
    const policyIds = appConfig.integrations.iubenda.policyId as PolicyIdMap;

    // ID di fallback
    const fallbackPolicyId = policyIds['it'] ?? Object.values(policyIds)[0] ?? 0;

    // Verifico se la lingua fornita esiste nella mappa
    if (lng && policyIds[lng]) {
        return policyIds[lng];
    } else {
        if (lng) {
            console.warn(
                `Policy ID for language '${lng}' not found in appConfig.integrations.iubenda.policyId. Using fallback ID: ${fallbackPolicyId}.`
            );
        }
        return fallbackPolicyId;
    }
};


interface PolicyPageRouteProps {
    policyType: 'privacy' | 'cookies';
}

/**
 * Componente che funge da elemento per le route /:lng/privacy e /:lng/cookies.
 * Legge il parametro :lng dall'URL, determina l'ID policy Iubenda corretto
 * e renderizza il componente Privacy o Cookies appropriato.
 */
const PolicyPageRoute: React.FC<PolicyPageRouteProps> = ({ policyType }) => {
    // 1. Ottiene il parametro 'lng' dall'URL
    const { lng } = useParams<{ lng: string }>();

    // 2. Ottiene l'ID policy corretto usando la funzione helper
    const policyId = getPolicyIdForLang(lng);

    // 3. Renderizza il componente corretto (Privacy o Cookies) in base alla prop `policyType`
    if (policyType === 'privacy') {
        return <Privacy agencyId={AGENCY_ID} policy={policyId} isPrivacy={true} />;
    } else if (policyType === 'cookies') {
        return <Cookies agencyId={AGENCY_ID} policy={policyId} isPrivacy={false} />;
    } else {
        console.error("Invalid 'policyType' passed to PolicyPageRoute:", policyType);
        return null;
    }
};

export default PolicyPageRoute;